/**
 * Shared model for the redesigned mobile Customers screen (Trioty Customers
 * Mobile): the Governorate/None grouping, the sort comparators, and the
 * row-detail field list. Sibling of products-mobile-model.ts.
 *
 * Everything here is derived from the REAL SellerCustomerView fields the list
 * endpoint returns — name, ordersCount, totalSpent, createdAt, governorate — so
 * grouping, sorting and filtering never invent data the backend doesn't have.
 */

import type { SellerCustomerView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { keys } from "@/lib/i18n/portal";

export type CustomerItem = SellerCustomerView;

export type CustomersGroupBy = "governorate" | "none";
export type CustomersSort = "spend" | "orders" | "name" | "recent";
export type RowProp = "governorate" | "orders" | "since";

/** Sentinel group/chip key for customers with no governorate. */
export const NO_GOVERNORATE = "__none__";

/** Filter selections for the mobile Customers filter sheet — both facets are
 *  backed by real fields (governorate id + ordersCount). */
export type CustomersFilterState = {
  /** Selected governorate ids (NO_GOVERNORATE for the null bucket). */
  governorates: string[];
  /** Only customers with at least one order. */
  hasOrders: boolean;
};

export const EMPTY_CUSTOMERS_FILTERS: CustomersFilterState = {
  governorates: [],
  hasOrders: false,
};

export function activeFilterCount(f: CustomersFilterState): number {
  return f.governorates.length + (f.hasOrders ? 1 : 0);
}

export const GROUP_LABEL: Record<CustomersGroupBy, string> = {
  governorate: keys.customers.tableGovernorate,
  none: keys.orders.groupByNone,
};

export const SORT_LABEL: Record<CustomersSort, string> = {
  spend: keys.customers.sortTopSpend,
  orders: keys.customers.sortMostOrders,
  name: keys.customers.sortName,
  recent: keys.customers.sortRecent,
};

/** Localized governorate name (falls back to Arabic when English is missing). */
export function governorateName(c: CustomerItem, locale: Locale): string | null {
  const g = c.governorate;
  if (!g) return null;
  return locale === "ar" ? g.nameAr : (g.nameEn ?? g.nameAr);
}

/** Stable key for a customer's governorate bucket. */
export function governorateKey(c: CustomerItem): string {
  return c.governorate?.id ?? NO_GOVERNORATE;
}

/** Client-side comparators over the loaded page. */
export function sortComparator(
  sort: CustomersSort,
  locale: Locale,
): (a: CustomerItem, b: CustomerItem) => number {
  switch (sort) {
    case "orders":
      return (a, b) => b.ordersCount - a.ordersCount;
    case "name":
      return (a, b) =>
        a.name.localeCompare(b.name, locale === "ar" ? "ar" : "en", {
          sensitivity: "base",
        });
    case "recent":
      // Newest customers first (createdAt desc) — the list endpoint carries no
      // per-customer last-order date, so "recent" means recently added.
      return (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    case "spend":
    default:
      return (a, b) => b.totalSpent - a.totalSpent;
  }
}
